import { ModuleId } from "@xmiles/holopix-types";
import type {
  Hunyuan3dCreationMode,
  Hunyuan3dDirection,
  Hunyuan3dModelVersion,
  Hunyuan3dPolyCount,
  Hunyuan3dImageMode,
  Hunyuan3dSelectorOption,
  TextureStyle,
} from "./types";

const uploadTipsExampleImage = new URL(
  "@/assets/images/workspace/hunyuan3d/upload-tips-example.png",
  import.meta.url
).href;
const modelVersionV31Image = new URL(
  "@/assets/images/workspace/hunyuan3d/model-version-v31.png",
  import.meta.url
).href;
const modelVersionV30Image = new URL(
  "@/assets/images/workspace/hunyuan3d/model-version-v30.png",
  import.meta.url
).href;

/** 混元 3D 模块 ID，对齐 @xmiles/holopix-types 的 CanvasGenerate3d（73，自由画布-3D生成） */
export const HUNYUAN_3D_MODULE_ID = Number(ModuleId.CanvasGenerate3d);

export const HUNYUAN_3D_DIRECTION_OPTIONS: Hunyuan3dSelectorOption[] = [
  { value: "text-to-3d", label: "混元文生3D", icon: "icon-a-wensheng3D" },
  { value: "image-to-3d", label: "混元图生3D", icon: "icon-a-tusheng3D" },
];

export const HUNYUAN_3D_MODEL_VERSION_OPTIONS: Hunyuan3dSelectorOption[] = [
  {
    value: "v3.1",
    label: "混元3D v3.1",
    image: modelVersionV31Image,
    description: "几何更精细，纹理更还原，更多视图可控生成。",
  },
  {
    value: "v3.0",
    label: "混元3D v3.0",
    image: modelVersionV30Image,
    description: "36亿体素，复杂物体高精建模，提升人物质感。",
  },
];

export const HUNYUAN_3D_POLY_COUNT_OPTIONS: Hunyuan3dSelectorOption[] = [
  { value: "1.5m", label: "150万面" },
  { value: "1m", label: "100万面" },
  { value: "500k", label: "50万面" },
  { value: "50k", label: "5万面" },
];

export const HUNYUAN_3D_IMAGE_MODE_OPTIONS: Hunyuan3dSelectorOption[] = [
  { value: "single", label: "单图生成", icon: "icon-dantushengcheng" },
  { value: "multi", label: "多图生成", icon: "icon-duotushengcheng" },
];

/** UI 创作模式选项（文生3D / 单图生3D / 多图生3D），提交时再映射到 direction/imageMode */
export const HUNYUAN_3D_CREATION_MODE_OPTIONS: Hunyuan3dSelectorOption[] = [
  { value: "text", label: "文生3D", icon: "icon-a-wensheng3D" },
  { value: "single-image", label: "单图生3D", icon: "icon-dantushengcheng" },
  { value: "multi-image", label: "多图生3D", icon: "icon-duotushengcheng" },
];

// TODO: 缩略图待设计切图/CDN 资源就绪后填充（thumbnail 为空时 UI 显示占位块）
export const HUNYUAN_3D_TEXTURE_STYLES: TextureStyle[] = [
  { key: "general", label: "通用", thumbnail: "" },
  { key: "stone", label: "石雕", thumbnail: "" },
  { key: "porcelain", label: "青花瓷", thumbnail: "" },
  { key: "chinese", label: "中国风", thumbnail: "" },
  { key: "cartoon", label: "卡通", thumbnail: "" },
  { key: "standing", label: "站立", thumbnail: "" },
];

export const DEFAULT_TEXTURE_STYLE_KEY = HUNYUAN_3D_TEXTURE_STYLES[0].key;

export const HUNYUAN_3D_DEFAULTS = {
  direction: "text-to-3d" as Hunyuan3dDirection,
  modelVersion: "v3.1" as Hunyuan3dModelVersion,
  polyCount: "50k" as Hunyuan3dPolyCount,
  imageMode: "single" as Hunyuan3dImageMode,
  textureStyleKey: DEFAULT_TEXTURE_STYLE_KEY,
};

const TEXT_TO_3D_PLACEHOLDER = "请描述你想生成的内容，建议单主体为主。例如：一只黄色的拉布拉多雕塑...";
// NOTE: 单图模式文案；多图模式的 placeholder 由 multiview profile 提供（见 multiview/constants.ts）
const IMAGE_TO_3D_PLACEHOLDER = "图生 3D 暂不支持文字提示。建议上传背景简洁、主体单一的图片，并确保主体清晰且占画面较大比例。";

/** 按生成方向返回输入框 placeholder 文案 */
export const getHunyuan3dPlaceholder = (direction: Hunyuan3dDirection): string => {
  return direction === "text-to-3d" ? TEXT_TO_3D_PLACEHOLDER : IMAGE_TO_3D_PLACEHOLDER;
};

/**
 * UI 创作模式 → 既有提交字段（direction / imageMode）。
 * 文生不带 imageMode；图生单图/多图仅改变 imageMode，不引入新 API 字段。
 */
export const toHunyuan3dParams = (
  mode: Hunyuan3dCreationMode
): { direction: Hunyuan3dDirection; imageMode?: Hunyuan3dImageMode } => {
  if (mode === "text") return { direction: "text-to-3d" };
  if (mode === "multi-image") return { direction: "image-to-3d", imageMode: "multi" };
  return { direction: "image-to-3d", imageMode: "single" };
};

/** 既有 direction / imageMode → UI 创作模式；图生缺省 imageMode 时按单图 */
export const getHunyuan3dCreationMode = (
  direction: Hunyuan3dDirection,
  imageMode: Hunyuan3dImageMode = HUNYUAN_3D_DEFAULTS.imageMode
): Hunyuan3dCreationMode => {
  if (direction === "text-to-3d") return "text";
  return imageMode === "multi" ? "multi-image" : "single-image";
};

/** 图片上传建议（图生3D 单图模式 popover 文案） */
export const HUNYUAN_3D_UPLOAD_TIPS = {
  title: "图片上传建议",
  description: "支持png/jpg/jpeg/webp格式，图片大小最大不超过10M，分辨率最低要求128x128",
  checklist: ["背景简单（尽量纯色）", "不包含文字", "单主体", "主体不要过小"],
  exampleImage: uploadTipsExampleImage,
};

/** 单图/文生3D 每次消耗算力（腾讯云 35 积分 → Holopix 525） */
export const HUNYUAN_3D_POWER_BASE = 525;
/** 多视角生3D 每次消耗算力（腾讯云 45 积分 → Holopix 675） */
export const HUNYUAN_3D_POWER_MULTI_VIEW = 675;

/**
 * 混元 3D 算力计算器（注册进 useCreditsCalculation 的 POWER_CALCULATOR_MAP）。
 * 业务规则：携带 multiViewImages（多视角补充图）时按多视角档计费，否则按单图/文生档。
 * NOTE: 规则实现归属混元 3D 业务域；@xmiles/holopix-helper 同名函数为旧签名占位，勿委托。
 */
export const getHunyuan3dConsumePower = (params?: {
  multiViewImages?: unknown[];
  /** @deprecated 不再参与算力计算，保留仅为兼容既有调用方 */
  modelVersion?: Hunyuan3dModelVersion;
  /** @deprecated 不再参与算力计算，保留仅为兼容既有调用方 */
  polyCount?: Hunyuan3dPolyCount;
}): number => {
  if (Array.isArray(params?.multiViewImages) && params.multiViewImages.length > 0) {
    return HUNYUAN_3D_POWER_MULTI_VIEW;
  }
  return HUNYUAN_3D_POWER_BASE;
};

// ========== UI ↔ API 参数映射 ==========

/** UI polyCount 档位 → 后端 faceCount 整数值（CanvasGenerate3dService.generate 参数） */
const POLY_COUNT_TO_FACE_COUNT: Record<Hunyuan3dPolyCount, number> = {
  "1.5m": 1_500_000,
  "1m": 1_000_000,
  "500k": 500_000,
  "50k": 50_000,
};

/** 由正向表派生，保证 faceCount ↔ polyCount 单一数据源 */
const FACE_COUNT_TO_POLY_COUNT = Object.fromEntries(
  (Object.entries(POLY_COUNT_TO_FACE_COUNT) as Array<[Hunyuan3dPolyCount, number]>).map(
    ([poly, face]) => [face, poly]
  )
) as Record<number, Hunyuan3dPolyCount>;

export const polyCountToFaceCount = (polyCount: Hunyuan3dPolyCount): number => {
  return POLY_COUNT_TO_FACE_COUNT[polyCount] ?? POLY_COUNT_TO_FACE_COUNT[HUNYUAN_3D_DEFAULTS.polyCount];
};

/** UI modelVersion ↔ 后端 hunyuanModel（单一数据源） */
const MODEL_VERSION_TO_HUNYUAN_MODEL: Record<Hunyuan3dModelVersion, "3.0" | "3.1"> = {
  "v3.0": "3.0",
  "v3.1": "3.1",
};

const HUNYUAN_MODEL_TO_MODEL_VERSION = Object.fromEntries(
  (Object.entries(MODEL_VERSION_TO_HUNYUAN_MODEL) as Array<[Hunyuan3dModelVersion, "3.0" | "3.1"]>).map(
    ([version, model]) => [model, version]
  )
) as Record<"3.0" | "3.1", Hunyuan3dModelVersion>;

/** API faceCount / UI polyCount 字符串 → UI 档位；无法识别时回落默认档 */
export const faceCountToPolyCount = (value: unknown): Hunyuan3dPolyCount => {
  if (typeof value === "number" && Number.isFinite(value) && FACE_COUNT_TO_POLY_COUNT[value]) {
    return FACE_COUNT_TO_POLY_COUNT[value];
  }

  const raw = String(value ?? "").trim().toLowerCase();
  if ((Object.keys(POLY_COUNT_TO_FACE_COUNT) as Hunyuan3dPolyCount[]).includes(raw as Hunyuan3dPolyCount)) {
    return raw as Hunyuan3dPolyCount;
  }

  const asNumber = Number(raw);
  if (Number.isFinite(asNumber) && FACE_COUNT_TO_POLY_COUNT[asNumber]) {
    return FACE_COUNT_TO_POLY_COUNT[asNumber];
  }

  // 兼容拼接文案；面数从大到小匹配，避免 1500000 误命中 50000
  const faceEntries = (
    Object.entries(POLY_COUNT_TO_FACE_COUNT) as Array<[Hunyuan3dPolyCount, number]>
  ).sort((a, b) => b[1] - a[1]);
  for (const [poly, face] of faceEntries) {
    if (raw.includes(String(face))) return poly;
  }

  return HUNYUAN_3D_DEFAULTS.polyCount;
};

/** UI modelVersion → 后端 hunyuanModel 字段值 */
export const modelVersionToHunyuanModel = (version: Hunyuan3dModelVersion): "3.0" | "3.1" => {
  return MODEL_VERSION_TO_HUNYUAN_MODEL[version] ?? MODEL_VERSION_TO_HUNYUAN_MODEL[HUNYUAN_3D_DEFAULTS.modelVersion];
};

/** API hunyuanModel / UI modelVersion → UI 版本；无法识别时回落默认版本 */
export const hunyuanModelToModelVersion = (value: unknown): Hunyuan3dModelVersion => {
  const raw = String(value ?? "").trim().toLowerCase();
  // 统一去掉可选的 v 前缀再精确匹配契约值
  const normalized = raw.replace(/^v/, "") as "3.0" | "3.1";
  if (HUNYUAN_MODEL_TO_MODEL_VERSION[normalized]) {
    return HUNYUAN_MODEL_TO_MODEL_VERSION[normalized];
  }
  return HUNYUAN_3D_DEFAULTS.modelVersion;
};

// ========== API 结果字段兼容 ==========

/**
 * 从生成结果/图片记录中解析 GLB 模型地址。
 * 兼容后端新字段 glbUrl 与旧字段 glb；契约定稿收敛为单字段时只需改这里。
 */
export const resolveGlbUrl = (raw: unknown): string | undefined => {
  const item = raw as { glbUrl?: unknown; glb?: unknown } | null | undefined;
  const url = String(item?.glbUrl || item?.glb || "").trim();
  return url || undefined;
};

/**
 * 从生成结果/图片记录中解析 OBJ 下载地址（通常为 zip）。
 * 兼容 objUrl / obj_url / obj（与 glbUrl/glb 对称；versionList 返回 obj）。
 */
export const resolveObjUrl = (raw: unknown): string | undefined => {
  const item = raw as { objUrl?: unknown; obj_url?: unknown; obj?: unknown } | null | undefined;
  const url = String(item?.objUrl || item?.obj_url || item?.obj || "").trim();
  return url || undefined;
};
